/**
 * Unified Translation Service
 *
 * A generalized client for translating documents from any source language (auto-detected)
 * to any target language using Claude.
 * Model selection controlled by TESTING_MODE in agenticWorkflowConfig.ts
 * This client preserves document structure, formatting, and hierarchy during translation.
 * Stateless - each call is a fresh, independent request with no memory of previous sessions.
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import {
  TRANSLATION_PRIMARY,
} from '../config/modelTiers.generated';
import {
  CLAUDE_TRANSLATOR_MODEL,
  CLAUDE_TRANSLATOR_FALLBACK,
  TESTING_MODE,
  ENABLE_EXTENDED_THINKING,
  getThinkingConfig,
  TRANSLATION_MAX_TOKENS,
  TRANSLATION_TOTAL_TIMEOUT_MS,
  TRANSLATION_CHUNK_TIMEOUT_MS,
  TRANSLATION_TIMEOUT_DESCRIPTION,
} from '../config/agenticWorkflowConfig';
import { sanitizeProviderNames } from '../config/providerCodeNames';
import {
  isProviderPolicyBlocked,
  isPayableUpstreamFailure,
  scrubUpstreamErrorText,
  PROVIDER_POLICY_USER_MESSAGE,
} from '../config/providerPolicyErrors';
import {
  segmentSourceForTranslation,
  splitManifestForTranslation,
  rejoinManifest,
  TRANSLATION_SEGMENT_CHARS,
} from '../config/translationSegmentation';
import { runTaskStream } from './openRouterClient';
import { knownAccountHint } from './accountHint';
import { SERVER_METER_HEADERS } from './proxyMeter';
import { proxyAuthHeaders } from './proxyAuth';

/** Ledger-row summary for the proxy's server meter (slice 2, 2026-09-15). */
const TRANSLATION_METER_SUMMARY = 'translation';

// Claude model identifiers for translation.
// 2026-05-10 (Miles directive, reverses REL-006-5): Opus-4-7 across both
// branches. NO Sonnet/Haiku in primary OR fallback. Cross-provider performance
// ladder (GPT-5.5 → Gemini-3.1-Pro) handles Claude outages via OpenRouter.
const CLAUDE_MODEL =
  process.env.REACT_APP_ANTHROPIC_TRANSLATOR_MODEL ||
  CLAUDE_TRANSLATOR_MODEL;
const CLAUDE_MODEL_FALLBACK =
  process.env.REACT_APP_ANTHROPIC_TRANSLATOR_FALLBACK ||
  CLAUDE_TRANSLATOR_FALLBACK;
// Additional fallback for maximum reliability — stay on the ROUTE primary per
// directive. (2026-08-22: was TIER1_PRIMARY, which had silently become the
// default-spawn grok — the "stay on Opus" comment above it was no longer true.)
const CLAUDE_MODEL_FALLBACK_2 = TESTING_MODE
  ? TRANSLATION_PRIMARY
  : TRANSLATION_PRIMARY;

// Get the Firebase proxy URL for Anthropic API calls (to avoid CORS)
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}


// ============================================================================
// Translation Manifest Types
// ============================================================================

/**
 * Structure containing ALL translatable text elements for a document.
 * This ensures titles, dates, footers, and metadata are translated alongside body content.
 */
export interface TranslationManifest {
  /** Document title */
  title: string;
  /** Pre-formatted date (e.g., "January 15, 2024") */
  date: string;
  /** "Prepared by: Name" line (empty if not applicable) */
  preparedBy: string;
  /** Law firm/organization name */
  firmName: string;
  /** Full copyright notice for cover page */
  copyrightNotice: string;
  /** Short copyright for page footers */
  copyrightShort: string;
  /** Page number format string with placeholders (e.g., "Page {current}") */
  pageNumberFormat: string;
  /** The main document body content */
  bodyContent: string;
}

/**
 * Translated version of the manifest with all text in the target language.
 */
export interface TranslatedManifest {
  /** Detected source language (e.g., "English", "Spanish", "Japanese") */
  detectedSourceLanguage: string;
  /** Translated document title */
  title: string;
  /** Target language formatted date (e.g., "15 de enero de 2024" for Spanish) */
  date: string;
  /** Translated "Prepared by" line (e.g., "Preparado por: Name" for Spanish) */
  preparedBy: string;
  /** Firm name (typically stays unchanged) */
  firmName: string;
  /** Translated full copyright notice */
  copyrightNotice: string;
  /** Translated short copyright for footers */
  copyrightShort: string;
  /** Target language page number format (e.g., "Página {current}" for Spanish) */
  pageNumberFormat: string;
  /** Translated document body content */
  bodyContent: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default firm name used ONLY as a last-resort fallback for the Fernando
 * widget path. MT-QA-BUG-001: callers that know their tenant MUST pass a
 * firmName via `buildTranslationManifest({ firmName })` so T1/T2 reports do
 * not inherit Fernando branding.
 */
const DEFAULT_FIRM_NAME = 'The Law Offices of Fernando Hidalgo, Inc.';

/**
 * Default generated by attribution.
 * MT-QA-BUG-001 follow-up (2026-05-08 §132a regression): was the
 * placeholder "Legal AI Assistant"; replaced with the product brand
 * label so translated research exports read "Generated by: Prest0n".
 */
const DEFAULT_GENERATED_BY = 'Prest0n';

/**
 * Build the system prompt for high-fidelity translation to any target language.
 * This prompt instructs Claude to auto-detect the source language and translate
 * to the specified target language.
 */
export function buildTranslationSystemPrompt(targetLanguage: string): string {
  return `### SYSTEM ROLE
You are a High-Fidelity Localization Engine. Your objective is to translate a structured document manifest from the source language (which you must detect) to ${targetLanguage} with 100% preservation of document structure and formatting.

### INPUT FORMAT
You will receive a document with clearly marked sections using delimiters:

===SOURCE_LANGUAGE===
[Leave this empty in the input - you will detect and fill this in your output]
===TITLE===
[Document title to translate]
===DATE===
[Date - convert to ${targetLanguage} format conventions]
===PREPARED_BY===
[Generated by / Facilitated by line - translate appropriately for ${targetLanguage}]
===FIRM_NAME===
[Law firm name - keep as-is, do not translate proper business names]
===COPYRIGHT_NOTICE===
[Full copyright notice - translate all text except the firm name]
===COPYRIGHT_SHORT===
[Short footer copyright - translate all text except the firm name]
===PAGE_FORMAT===
[Page number format - translate "Page" to appropriate ${targetLanguage} word]
===BODY_CONTENT===
[Main document content to translate]
===END===

### CORE INSTRUCTIONS

1. **Detect Source Language**: Identify the language of the input content and output it in the SOURCE_LANGUAGE section (e.g., "English", "Spanish", "French", "Japanese", etc.).

2. **Output the EXACT same section structure** with translated content.

3. **Translate ALL text to professional ${targetLanguage}** (neutral/standard dialect).

4. **Date conversion**: Convert to ${targetLanguage} date format conventions.
   - Examples for different target languages:
   - Spanish: "January 15, 2024" → "15 de enero de 2024"
   - French: "January 15, 2024" → "15 janvier 2024"
   - German: "January 15, 2024" → "15. Januar 2024"
   - Japanese: "January 15, 2024" → "2024年1月15日"
   - Chinese: "January 15, 2024" → "2024年1月15日"
   - Portuguese: "January 15, 2024" → "15 de janeiro de 2024"
   - Italian: "January 15, 2024" → "15 gennaio 2024"
   - Korean: "January 15, 2024" → "2024년 1월 15일"
   - Arabic: "January 15, 2024" → "15 يناير 2024"
   - Russian: "January 15, 2024" → "15 января 2024 г."

5. **Page format**: Translate "Page" appropriately:
   - Spanish: "Página {current}"
   - French: "Page {current}"
   - German: "Seite {current}"
   - Japanese: "{current}ページ" or "ページ {current}"
   - Chinese: "第{current}页"
   - Portuguese: "Página {current}"
   - Italian: "Pagina {current}"
   - Korean: "{current}페이지"
   - Arabic: "صفحة {current}"
   - Russian: "Страница {current}"

6. **Preserve proper nouns**: Keep business names, personal names, and brand names unchanged.

7. **Preserve formatting**: Maintain all markdown, bullets, numbering, and whitespace in BODY_CONTENT.

8. **Copyright text**: Translate phrases like "All rights reserved" appropriately for ${targetLanguage}, but keep the firm name unchanged.
   - Spanish: "Todos los derechos reservados"
   - French: "Tous droits réservés"
   - German: "Alle Rechte vorbehalten"
   - Japanese: "無断転載を禁じます" or "全ての権利を留保"
   - Chinese: "版权所有"
   - Portuguese: "Todos os direitos reservados"
   - Italian: "Tutti i diritti riservati"
   - Korean: "모든 권리 보유"
   - Arabic: "جميع الحقوق محفوظة"
   - Russian: "Все права защищены"

### OUTPUT REQUIREMENTS
- Output ONLY the translated document in the EXACT same section format
- Preserve all section delimiters exactly as shown (===SOURCE_LANGUAGE===, ===TITLE===, ===DATE===, etc.)
- No introductory text, explanations, or closing remarks
- Begin directly with ===SOURCE_LANGUAGE===

[INSERT YOUR DOCUMENT TEXT HERE]`;
}

export interface TranslatorParams {
  /** The document content to translate */
  documentContent: string;
  /** The target language (e.g., "Spanish", "French", "Japanese") */
  targetLanguage: string;
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text and token usage */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /**
   * Called as whole-document sections finish, so a multi-minute run can show
   * how far along it is instead of an unchanging spinner (2026-08-12). Only
   * fires for segmented runs; a single-call translation has nothing to count.
   */
  onProgress?: (progress: TranslationProgress) => void;
  /**
   * Fires on every token delta from a body segment (2026-08-14).
   *
   * WHY: onProgress only fires when a segment FINISHES. With 25 segments at
   * concurrency 6 they finish in waves, so a 200-page run goes minutes at a
   * time with no callback at all — and the orchestrator's inactivity ceiling,
   * which can only see callbacks, concluded the turn was dead and aborted it
   * mid-run (Fernando's 200-page manual, twice, at section 14 of 25).
   *
   * Segments were already streaming over the wire; the stream client was
   * simply called without an onChunk handler and the deltas were discarded.
   * Forwarding them makes liveness EVIDENCE (real bytes arriving) rather than
   * an assumption — a blind keepalive timer would have kept a genuinely hung
   * run alive forever.
   */
  onSegmentActivity?: (info: { index: number; charsSoFar: number }) => void;
  /**
   * Transport liveness: fires whenever BYTES arrive, including extended-thinking
   * and keep-alive traffic that carries no text delta. onChunk cannot serve this
   * role — it requires content, and a high-effort model is silent on content for
   * minutes while it reasons. Three aborted 200-page runs came from treating
   * "no content" as "no life" (INCIDENT-20260815).
   */
  onStreamActivity?: () => void;

  /**
   * INTERNAL (2026-08-11): set when this call is the head segment of a
   * whole-document run, so it takes the single-call path instead of
   * re-entering segmentation. Not part of the public contract.
   */
  __segmentedHead?: boolean;
}

/** Live position of a whole-document run. */
export interface TranslationProgress {
  /** Sections fully translated so far, including the head section. */
  completedSections: number;
  /** Total sections this document was split into. */
  totalSections: number;
}

/**
 * What actually came back, measured against what was sent (2026-08-12).
 *
 * runWholeDocumentTranslation has always computed this to decide whether to
 * stamp an in-band INCOMPLETE marker; surfacing it lets the caller tell the
 * customer plainly how much of their document was translated instead of
 * leaving them to count paragraphs themselves.
 */
export interface TranslationCoverage {
  /** Sections the document was split into. */
  totalSections: number;
  /** Sections that came back materially short of their source. */
  shortSections: number;
  /** Sections that failed outright (ladder exhausted, or budget spent). */
  failedSections: number;
  /** Translated characters ÷ source characters. */
  ratio: number;
  sourceChars: number;
  translatedChars: number;
  /** True when the run met MIN_COMPLETENESS_RATIO with nothing short or failed. */
  complete: boolean;
}

export interface TranslatorResult {
  text: string;
  raw?: unknown;
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
  /** Present only for whole-document (segmented) runs. */
  coverage?: TranslationCoverage;
}

/**
 * Build the user message with the document content inserted into the system prompt.
 */
function buildUserMessage(documentContent: string, targetLanguage: string): string {
  return buildTranslationSystemPrompt(targetLanguage).replace(
    '[INSERT YOUR DOCUMENT TEXT HERE]',
    documentContent || '[missing document content]'
  );
}

/**
 * Script/diacritic heuristic for the source language. Extracted from the Lite
 * path (2026-08-11) so the cross-provider fallback can report the same value
 * without duplicating the table.
 */
export function detectSourceLanguage(text: string): string {
  if (/[áéíóúñ¿¡]/i.test(text)) return 'Spanish';
  if (/[àâçéèêëîïôûùüÿœæ]/i.test(text)) return 'French';
  if (/[äöüß]/i.test(text)) return 'German';
  if (/[\u4e00-\u9fff]/.test(text)) return 'Chinese';
  if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) return 'Japanese';
  if (/[\uac00-\ud7af]/.test(text)) return 'Korean';
  if (/[\u0400-\u04ff]/.test(text)) return 'Russian';
  if (/[\u0600-\u06ff]/.test(text)) return 'Arabic';
  if (/[ãõ]/.test(text)) return 'Portuguese';
  return 'Unknown';
}

// Continuation discipline, shared by the primary loop and the cross-provider
// fallback (INCIDENT-20260811 — the fallback used to have none at all).
// Raised 5 → 8 (2026-06-05) so very long legal documents can fully complete.
const MAX_CONTINUATION_PASSES = 8;
/** A translation should be the same order of magnitude as its source; below
 *  this ratio an unsignalled stop is treated as a silent truncation. */
const MIN_COMPLETENESS_RATIO = 0.85;
/** A pass adding fewer than this many chars means the model is done/stuck. */
const MIN_PROGRESS_CHARS = 200;
/** Lite path: same discipline, shorter ceiling — transcriptions are smaller. */
const LITE_MAX_CONTINUATION_PASSES = 4;
const LITE_MIN_COMPLETENESS_RATIO = 0.85;
const LITE_MIN_PROGRESS_CHARS = 200;

/**
 * ── Whole-document translation (2026-08-11) ─────────────────────────────────
 *
 * Goal: ONE request translates the entire document, up to ~200 pages, with no
 * "ask me to continue" and no truncation.
 *
 * Sequential continuation cannot do it. A 200-page expediente is ~500k source
 * characters; at ~24k characters per model call that is ~21 calls, and at the
 * ~2 minutes a dense legal segment takes, a serial chain runs past 40 minutes —
 * far outside any wall clock a user (or a browser tab) will hold.
 *
 * So the body is segmented on structural boundaries and the segments are
 * translated CONCURRENTLY, then reassembled in order. ~21 segments at
 * concurrency 6 is four waves — roughly 8 minutes, inside the budget below.
 *
 * Two things make concurrency safe here:
 *   - segments are independent units of prose, and the per-segment prompt
 *     carries the same instructions, so terminology stays consistent;
 *   - the manifest header (title/date/copyright — the parts every downstream
 *     deliverable parses) rides the FIRST call together with segment 0, so the
 *     structured shape the caller expects comes back exactly as before.
 */

/** How many segments translate at once. Six keeps a 200-page document inside
 *  the budget without opening an unreasonable number of upstream calls. */
export const TRANSLATION_SEGMENT_CONCURRENCY = 6;

/** Wall clock for a segmented run, anchored before the first segment call.
 *  Generous by design: finishing the document is the point. */
export const SEGMENTED_TRANSLATION_BUDGET_MS = 25 * 60 * 1000;

/** Map with bounded concurrency, preserving input order in the output. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

/** Continuation passes allowed PER SEGMENT (2026-08-12). See translateBodySegment. */
const SEGMENT_MAX_CONTINUATION_PASSES = 6;
/** Safety bound on the task-mode rung walk — the server's TASK_LADDER_EXHAUSTED
 *  ends descent at the real ladder length; this only guards against a server
 *  that never says so. Comfortably above any real tier. */
const SEGMENT_MAX_TASK_RUNGS = 8;
/** Below this, a segment is materially short enough to mark in-band. */
const SEGMENT_MARK_INCOMPLETE_RATIO = 0.7;

/**
 * How many times a short section may be halved and retried.
 *
 * WHY (2026-08-20, the 200-page manual): pages 68-72, 140-143 and 188-191 were
 * missing from the delivered document — three sections that stopped early and
 * were ACCEPTED. When a model will not finish a chunk, the most reliable
 * remedy is to hand it a smaller chunk; halving twice takes a 24K-char section
 * down to 6K, which is well inside what every rung completes.
 */
export const SEGMENT_MAX_BISECT_DEPTH = 2;
/** Below this there is nothing to gain from halving — the model is not truncating. */
export const SEGMENT_MIN_BISECT_CHARS = 4_000;

/**
 * Split a segment in two for a retry, preferring a paragraph boundary.
 *
 * Cutting mid-sentence is survivable (the segment prompt already says a
 * section may begin or end mid-sentence) but a paragraph break costs nothing
 * and keeps both halves readable, so look for the one nearest the midpoint.
 */
export function splitSegmentForRetry(segment: string): [string, string] | null {
  const text = segment || '';
  if (text.length < SEGMENT_MIN_BISECT_CHARS) return null;
  const midpoint = Math.floor(text.length / 2);
  const window = Math.floor(text.length / 4);

  const nearestBreak = (needle: string): number => {
    let best = -1;
    let from = text.indexOf(needle);
    while (from !== -1) {
      if (Math.abs(from - midpoint) <= window &&
          (best === -1 || Math.abs(from - midpoint) < Math.abs(best - midpoint))) {
        best = from;
      }
      if (from > midpoint + window) break;
      from = text.indexOf(needle, from + 1);
    }
    return best;
  };

  const cut = (() => {
    const paragraph = nearestBreak('\n\n');
    if (paragraph > 0) return paragraph + 2;
    const line = nearestBreak('\n');
    if (line > 0) return line + 1;
    const space = nearestBreak(' ');
    if (space > 0) return space + 1;
    return midpoint;
  })();

  const head = text.slice(0, cut);
  const tail = text.slice(cut);
  if (!head.trim() || !tail.trim()) return null;
  return [head, tail];
}

interface SegmentTranslation {
  text: string;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  model: string;
  failed: boolean;
  /** Translated chars ÷ source chars for this segment. */
  ratio: number;
  /** False when the segment stopped short even after its continuation passes. */
  complete: boolean;
}

/**
 * Model output that is a REFUSAL or a question rather than a translation
 * (INCIDENT-20260812).
 *
 * A mis-detected target language ("Scanned", from the article in "a scanned
 * medical file") made the model reply "'Scanned' is a document format, not a
 * language… please specify a valid target language" — and nineteen paragraphs
 * of that objection were delivered to a client as their translated document.
 * The model behaved correctly; the pipeline treated its objection as content.
 *
 * Deliberately narrow: anchored near the start of the output and requiring
 * meta-language about translating, so a legitimate translation that happens to
 * discuss languages is not discarded.
 */
const REFUSAL_SHAPED_OUTPUT =
  new RegExp(
    [
      // "'Scanned' is a document format, not a language"
      'is (?:a|an) [\\w-]+ (?:format|file type),? not a language',
      'please (?:specify|provide|confirm) a valid target language',
      "i (?:cannot|can't|am unable to) translate",
      '(?:specify|confirm|provide) (?:the|a) (?:desired )?(?:output|target) language',
      // Analyzer-failure phrasings (INCIDENT-20260812, second round): the
      // vision path returns its own apology as if it were content — "No
      // document was attached or accessible. Please upload the document image
      // or PDF…" — and it was delivered as a 1 KB PDF, blaming the client for
      // not attaching a file they had attached.
      'no document (?:was|has been) (?:attached|provided|accessible)',
      'please upload (?:the|a|your) (?:document|file|image|pdf)',
      'no (?:text|content) (?:could be|was) (?:extracted|found|read)',
      '(?:i )?(?:do not|don\'t) see (?:any|an) (?:attached )?(?:document|file|image)',
    ].join('|'),
    'i',
  );

export function isRefusalShapedTranslation(text: string): boolean {
  const head = (text || '').trim().slice(0, 600);
  return REFUSAL_SHAPED_OUTPUT.test(head);
}

/**
 * Translate ONE body segment, descending the ladder on failure AND continuing
 * the segment until its output is proportionate to its source.
 *
 * WHY the continuation loop (measured on the live 200-page run, 2026-08-12):
 * with a single call per segment, the delivered document contained 633 of the
 * source's 1,896 numbered paragraphs and 71 of its 200 pages — in a telltale
 * regular pattern of roughly the first 2 pages of every 6-page segment. The
 * models were simply stopping early, well under the 32K token ceiling, and
 * nothing was watching.
 *
 * The primary (head) leg never had that problem because runTranslation's
 * completeness guard forces continuation passes when output comes back
 * materially shorter than its source. This applies the same discipline
 * per segment: keep asking the model to resume while the output is short AND
 * the last pass still produced meaningful new text.
 */
/**
 * A section that is still materially short says so where the reader can see
 * it, rather than quietly deleting two thirds of a legal file.
 */
function markIfShort(text: string, ratio: number, index: number, total: number): string {
  if (ratio >= SEGMENT_MARK_INCOMPLETE_RATIO) return text;
  return `${text}\n\n[SECTION ${index + 1} of ${total} INCOMPLETE — the model stopped early here. Ask me to retry this section.]`;
}

async function translateBodySegment(params: {
  segment: string;
  index: number;
  total: number;
  targetLanguage: string;
  maxTokens: number;
  signal?: AbortSignal;
  /** Continuation passes for THIS segment (tests pin 1 for single-shot). */
  maxPasses?: number;
  /** Token-level liveness for the caller; see TranslatorParams.onSegmentActivity. */
  onActivity?: (info: { index: number; charsSoFar: number }) => void;
  /** Halving depth reached so far; see SEGMENT_MAX_BISECT_DEPTH. */
  bisectDepth?: number;
}): Promise<SegmentTranslation> {
  const systemPrompt =
    `${buildSimplifiedTranslationPrompt(params.targetLanguage)}\n\n` +
    `This is SECTION ${params.index + 1} of ${params.total} of a longer document. ` +
    `Translate only what is given. It may start or end mid-sentence — continue it as-is. ` +
    `Do not summarize, do not add transitions, and do not restate anything from other sections.`;

  const firstMessage = { role: 'user' as const, content: `${systemPrompt}\n\n${params.segment}` };
  const sourceLength = params.segment.length;
  const maxPasses = params.maxPasses ?? SEGMENT_MAX_CONTINUATION_PASSES;

  let lastError: unknown;
  // The best (longest relative to source) result any rung produced, and what
  // every attempt cost — a section that took three rungs billed three rungs.
  let best: { text: string; model: string; ratio: number } | null = null;
  let spentInputTokens = 0;
  let spentOutputTokens = 0;
  let spentReasoningTokens = 0;
  // D14 slice 5b-ii (2026-08-26): the descent walks RUNG OFFSETS in task
  // mode — the route name + rung are the only routing inputs (the server
  // resolves each rung's model and effort; the #1229 contract), while the
  // descent DECISION stays right here, driven by the same output judgment
  // as always: refusal shape, completeness ratio, stall detection. A 400
  // TASK_LADDER_EXHAUSTED ends the walk (nothing below that rung), and an
  // INJECTION_BLOCKED is content-level — no rung will change it. Slice 7
  // (2026-09-15) retired the legacy client-side model walk — an
  // INVALID_TASK_ROUTE now surfaces as real breakage instead of being
  // masked by a shadow ladder.
  type SegmentAttempt = { kind: 'task'; rung: number };
  const attempts: SegmentAttempt[] = Array.from(
    { length: SEGMENT_MAX_TASK_RUNGS },
    (_, rung) => ({ kind: 'task' as const, rung }),
  );
  for (const attempt of attempts) {
    const label = `translation#${attempt.rung}`;
    try {
      let accumulated = '';
      let inputTokens = 0;
      let outputTokens = 0;
      let reasoningTokens = 0;
      let servedModel = '';

      for (let pass = 0; pass < maxPasses; pass++) {
        const prior = accumulated;
        const passMessages = prior
          ? [
              firstMessage,
              { role: 'assistant' as const, content: prior },
              {
                role: 'user' as const,
                content:
                  'Continue translating this same section from exactly where you stopped. ' +
                  'Do not repeat anything you already produced, do not summarize, and do not ' +
                  'add commentary — resume mid-sentence if that is where the previous output ended.',
              },
            ]
          : [firstMessage];
        // Content deltas advance the visible character count.
        const onChunk = (_delta: string, accumulatedSoFar: string) => {
          params.onActivity?.({
            index: params.index,
            charsSoFar: prior.length + (accumulatedSoFar || '').length,
          });
        };
        // BYTES are the liveness signal. Content deltas are not enough: at
        // reasoning effort 'high' a 24K-char segment can spend minutes
        // emitting reasoning tokens before its first content token, and the
        // provider's keep-alives carry no content at all. Reporting only
        // content left the run looking dead while its socket was busy — the
        // 200-page manual was aborted three times that way.
        const onActivity = () => {
          params.onActivity?.({ index: params.index, charsSoFar: prior.length });
        };
        const result = await runTaskStream({
          route: 'translation',
          meterSummary: TRANSLATION_METER_SUMMARY,
          rung: attempt.rung,
          messages: passMessages,
          maxTokens: params.maxTokens,
          temperature: 0.2,
          signal: params.signal,
          onChunk,
          onActivity,
        });

        const raw = result.text || '';
        const passText = prior ? raw.trimEnd() : raw.trim();
        if (!prior && !passText) throw new Error(`empty translation for section ${params.index + 1}`);
        // A refusal is not a translation. Throwing here descends the ladder and,
        // if every rung refuses, marks the section FAILED — so the objection is
        // counted against coverage instead of being shipped as the document.
        if (!prior && isRefusalShapedTranslation(passText)) {
          throw new Error(
            `model refused to translate section ${params.index + 1} (target language may be invalid)`,
          );
        }
        // Join without gluing words together when the model resumes mid-sentence.
        const needsSpace = Boolean(prior) && !/\s$/.test(prior) && !/^\s/.test(passText);
        accumulated = prior ? `${prior}${needsSpace ? ' ' : ''}${passText}` : passText;

        servedModel = result.tokenUsage?.model || servedModel;
        inputTokens += result.tokenUsage?.inputTokens || 0;
        outputTokens += result.tokenUsage?.outputTokens || 0;
        reasoningTokens += result.tokenUsage?.reasoningTokens || 0;

        const ratio = sourceLength > 0 ? accumulated.length / sourceLength : 1;
        const madeProgress = passText.length >= MIN_PROGRESS_CHARS;
        if (ratio >= MIN_COMPLETENESS_RATIO) break;
        if (!madeProgress) {
          // The model has nothing left to give; forcing more passes just spins.
          console.warn(
            `[Translator] section ${params.index + 1}/${params.total} stalled at ratio ` +
            `${ratio.toFixed(2)} after pass ${pass + 1} (+${passText.length} chars) — accepting`,
          );
          break;
        }
        if (pass === maxPasses - 1) {
          console.warn(
            `[Translator] section ${params.index + 1}/${params.total} hit the continuation ceiling ` +
            `(${maxPasses} passes, ratio ${ratio.toFixed(2)})`,
          );
        } else {
          console.log(
            `[Translator] section ${params.index + 1}/${params.total} continuing ` +
            `(pass ${pass + 2}/${maxPasses}, ratio ${ratio.toFixed(2)})`,
          );
        }
      }

      const ratio = sourceLength > 0 ? accumulated.length / sourceLength : 1;
      const complete = ratio >= MIN_COMPLETENESS_RATIO;

      spentInputTokens += inputTokens;
      spentOutputTokens += outputTokens;
      spentReasoningTokens += reasoningTokens;

      if (complete) {
        return {
          text: accumulated,
          inputTokens: spentInputTokens,
          outputTokens: spentOutputTokens,
          reasoningTokens: spentReasoningTokens,
          model: servedModel,
          failed: false,
          ratio,
          complete,
        };
      }

      // A SHORT SECTION IS WHAT THE LADDER IS FOR (2026-08-20). This used to
      // return here regardless, so the fallback rungs only ever handled
      // errors — and a section the primary truncated was accepted on the spot.
      // That is how pages 68-72, 140-143 and 188-191 left the 200-page manual:
      // three sections stopped early and nothing tried again.
      if (!best || ratio > best.ratio) {
        best = { text: accumulated, model: servedModel, ratio };
      }
      console.warn(
        `[Translator] section ${params.index + 1}/${params.total} came back short on ${label} ` +
        `(ratio ${ratio.toFixed(2)}) — descending to the next rung`,
      );
    } catch (err: any) {
      if ((err as any)?.code === 'TASK_LADDER_EXHAUSTED') {
        // The server has no rung below this one — descent is over; the
        // bisect/best handling below takes it from here.
        break;
      }
      if ((err as any)?.code === 'INVALID_TASK_ROUTE') {
        // The route is unknown to the door — every rung fails identically,
        // and since slice 7 there is no shadow ladder to mask it. Surface
        // the breakage (SSOT/route drift) instead of retrying.
        lastError = err;
        break;
      }
      lastError = err;
      console.warn(
        `[Translator] section ${params.index + 1}/${params.total} failed on ${label}: ${err?.message}`,
      );
      // Blocked content is content-level — no rung or model will change it.
      if ((err as any)?.code === 'INJECTION_BLOCKED') break;
      // Every rung bills the same account — a payable failure will not improve.
      if (isPayableUpstreamFailure(err)) break;
    }
  }
  // Every rung truncated this section. A model that will not finish a chunk
  // will usually finish half of it, so halve it and translate the halves.
  const depth = params.bisectDepth ?? 0;
  const halves = depth < SEGMENT_MAX_BISECT_DEPTH ? splitSegmentForRetry(params.segment) : null;
  if (halves && !params.signal?.aborted) {
    console.warn(
      `[Translator] section ${params.index + 1}/${params.total} still short after the ladder — ` +
      `splitting into 2 and retrying (depth ${depth + 1}/${SEGMENT_MAX_BISECT_DEPTH})`,
    );
    const parts: SegmentTranslation[] = [];
    for (const half of halves) {
      parts.push(await translateBodySegment({ ...params, segment: half, bisectDepth: depth + 1 }));
    }
    const joined = parts.map((p) => p.text).join('\n\n');
    const splitRatio = sourceLength > 0 ? joined.length / sourceLength : 1;
    // Only keep the split result if it actually recovered more than the whole
    // -segment attempts did; otherwise the halving was wasted and the best
    // single attempt stands.
    if (!best || splitRatio > best.ratio) {
      const splitComplete = splitRatio >= MIN_COMPLETENESS_RATIO && parts.every((p) => !p.failed);
      return {
        text: markIfShort(joined, splitRatio, params.index, params.total),
        inputTokens: spentInputTokens + parts.reduce((n, p) => n + p.inputTokens, 0),
        outputTokens: spentOutputTokens + parts.reduce((n, p) => n + p.outputTokens, 0),
        reasoningTokens: spentReasoningTokens + parts.reduce((n, p) => n + p.reasoningTokens, 0),
        model: parts[0]?.model || '',
        failed: false,
        ratio: splitRatio,
        complete: splitComplete,
      };
    }
  }

  if (best) {
    console.warn(
      `[Translator] section ${params.index + 1}/${params.total} accepted short at ratio ` +
      `${best.ratio.toFixed(2)} after every rung and split`,
    );
    return {
      text: markIfShort(best.text, best.ratio, params.index, params.total),
      inputTokens: spentInputTokens,
      outputTokens: spentOutputTokens,
      reasoningTokens: spentReasoningTokens,
      model: best.model,
      failed: false,
      ratio: best.ratio,
      complete: false,
    };
  }

  console.error(
    `[Translator] section ${params.index + 1}/${params.total} exhausted the ladder: ${(lastError as any)?.message}`,
  );
  return {
    text: `[SECTION ${params.index + 1} of ${params.total} NOT TRANSLATED — this part could not be translated. Ask me to retry it.]`,
    inputTokens: spentInputTokens,
    outputTokens: spentOutputTokens,
    reasoningTokens: spentReasoningTokens,
    model: '',
    failed: true,
    ratio: 0,
    complete: false,
  };
}

/**
 * Translate a document too large for a single call: header + first segment in
 * one manifest-shaped call (so the structured response is unchanged), the
 * remaining segments concurrently, reassembled in order.
 */
async function runWholeDocumentTranslation(
  params: TranslatorParams,
  parts: ReturnType<typeof splitManifestForTranslation>,
  bodySegments: string[],
): Promise<TranslatorResult> {
  const startedAt = Date.now();
  const total = bodySegments.length;
  console.log(
    `[Translator] whole-document mode: ${total} sections, concurrency ${TRANSLATION_SEGMENT_CONCURRENCY}`,
  );
  // Announce the shape of the job before the first call: a 200-page document
  // can sit for a minute before any section lands, and "0 of 34" is far more
  // informative than a spinner that never changes.
  params.onProgress?.({ completedSections: 0, totalSections: total });

  // ── Head: manifest header + section 1, through the normal streaming path ──
  const headManifest = parts ? rejoinManifest(parts, bodySegments[0]) : bodySegments[0];
  const headResult = await runTranslation({
    ...params,
    documentContent: headManifest,
    // The head is one segment — it must not re-enter this branch.
    __segmentedHead: true,
    onComplete: undefined,
  } as TranslatorParams);

  // Split the TRANSLATED head manifest, not the source one: its header sections
  // (title, date, copyright, the model-filled SOURCE_LANGUAGE) are already in
  // the target language, and they are what the final document must carry.
  // Rejoining against the SOURCE header would have shipped a Spanish cover on
  // an English body.
  const translatedHeadParts = parts ? splitManifestForTranslation(headResult.text) : null;
  const headBody = translatedHeadParts
    ? translatedHeadParts.body.trim()
    : headResult.text.trim();
  params.onProgress?.({ completedSections: 1, totalSections: total });

  // ── Tail: every remaining section, concurrently ───────────────────────────
  const tailSegments = bodySegments.slice(1);
  const completed: string[] = new Array(tailSegments.length).fill('');
  let doneCount = 0;

  const tailResults = await mapWithConcurrency(
    tailSegments,
    TRANSLATION_SEGMENT_CONCURRENCY,
    async (segment, i) => {
      const elapsed = Date.now() - startedAt;
      const result =
        elapsed > SEGMENTED_TRANSLATION_BUDGET_MS
          ? ({
              text: `[SECTION ${i + 2} of ${total} NOT TRANSLATED — the translation budget was exhausted.]`,
              inputTokens: 0,
              outputTokens: 0,
              reasoningTokens: 0,
              model: '',
              failed: true,
              ratio: 0,
              complete: false,
            } as SegmentTranslation)
          : await translateBodySegment({
              segment,
              index: i + 1,
              total,
              targetLanguage: params.targetLanguage,
              maxTokens: Math.min(TRANSLATION_MAX_TOKENS, 32000),
              signal: params.signal,
              onActivity: params.onSegmentActivity,
            });
      completed[i] = result.text;
      doneCount++;
      // Report the in-ORDER prefix so the UI never shows sections out of
      // sequence while later ones are still in flight.
      let prefix = headBody;
      for (const piece of completed) {
        if (!piece) break;
        prefix += `\n\n${piece}`;
      }
      console.log(`[Translator] sections complete: ${doneCount + 1}/${total}`);
      // Budget-exhausted sections advance the counter too — a progress display
      // that freezes at 12/34 while the run is actually finishing is worse
      // than no display at all.
      params.onProgress?.({ completedSections: doneCount + 1, totalSections: total });
      params.onChunk?.(result.text, prefix);
      return result;
    },
  );

  let fullBody = [headBody, ...tailResults.map((r) => r.text)].join('\n\n').trim();
  const failedCount = tailResults.filter((r) => r.failed).length;
  const shortCount = tailResults.filter((r) => !r.failed && !r.complete).length;

  // ── Coverage measured in CONTENT, not in sections attempted (2026-08-12) ──
  // The live 200-page run reported success while delivering a third of the
  // document: every section had been *attempted*, so nothing looked wrong.
  // Compare translated length against source length and say so when the whole
  // document comes up materially short.
  const sourceLength = bodySegments.reduce((n, seg) => n + seg.length, 0);
  const coverageRatio = sourceLength > 0 ? fullBody.length / sourceLength : 1;
  const coverage: TranslationCoverage = {
    totalSections: total,
    shortSections: shortCount,
    failedSections: failedCount,
    ratio: coverageRatio,
    sourceChars: sourceLength,
    translatedChars: fullBody.length,
    complete: coverageRatio >= MIN_COMPLETENESS_RATIO && shortCount === 0 && failedCount === 0,
  };
  if (coverageRatio < MIN_COMPLETENESS_RATIO) {
    const pct = Math.round(coverageRatio * 100);
    console.warn(
      `[Translator] whole-document coverage ${pct}% (${fullBody.length}/${sourceLength} chars) — ` +
      `${shortCount} short section(s), ${failedCount} failed`,
    );
    fullBody +=
      `\n\n[INCOMPLETE TRANSLATION — roughly ${pct}% of the source was translated. ` +
      `${shortCount + failedCount} of ${total} sections came back short or failed. ` +
      `Ask me to retry and I will re-run the missing parts.]`;
  } else {
    console.log(
      `[Translator] whole-document coverage ${Math.round(coverageRatio * 100)}% ` +
      `(${fullBody.length}/${sourceLength} chars)`,
    );
  }

  const finalText = translatedHeadParts ? rejoinManifest(translatedHeadParts, fullBody) : fullBody;

  const tokenUsage: TokenUsageMetadata = {
    inputTokens:
      (headResult.tokenUsage?.inputTokens || 0) + tailResults.reduce((n, r) => n + r.inputTokens, 0),
    outputTokens:
      (headResult.tokenUsage?.outputTokens || 0) + tailResults.reduce((n, r) => n + r.outputTokens, 0),
    reasoningTokens:
      (headResult.tokenUsage?.reasoningTokens || 0) +
        tailResults.reduce((n, r) => n + r.reasoningTokens, 0) || undefined,
    model: headResult.tokenUsage?.model || TRANSLATION_PRIMARY,
    provider: 'claude',
    responseTimeMs: Date.now() - startedAt,
    timestamp: new Date(),
  };
  tokenUsage.totalTokens =
    (tokenUsage.inputTokens || 0) + (tokenUsage.outputTokens || 0) + (tokenUsage.reasoningTokens || 0) ||
    undefined;

  console.log(
    `[Translator] whole-document complete: ${total - failedCount}/${total} sections in ` +
    `${Math.round((Date.now() - startedAt) / 1000)}s`,
  );
  params.onComplete?.(finalText, tokenUsage);
  return { text: finalText, tokenUsage, coverage };
}

/**
 * Translate a document to any target language using Claude with streaming.
 *
 * Supports automatic continuation: if the model returns stop_reason="max_tokens"
 * (output cap hit mid-document), this function automatically sends a multi-turn
 * "continue" request, appending the new output to what was already streamed.
 * Handles documents of any length, up to MAX_CONTINUATION_PASSES.
 */
export async function runTranslation(params: TranslatorParams): Promise<TranslatorResult> {
  // ── Whole-document mode (2026-08-11) ──────────────────────────────────────
  // A document larger than one model call is segmented and translated
  // concurrently rather than through serial continuation passes, so ~200 pages
  // complete inside ONE request. Small documents keep the original streaming
  // single-call behaviour exactly (segmentSourceForTranslation returns one
  // segment, and this branch is skipped).
  if (!params.__segmentedHead) {
    const manifestParts = splitManifestForTranslation(params.documentContent);
    const bodyText = manifestParts ? manifestParts.body : params.documentContent;
    if (bodyText.length > TRANSLATION_SEGMENT_CHARS) {
      const bodySegments = segmentSourceForTranslation(bodyText);
      if (bodySegments.length > 1) {
        try {
          return await runWholeDocumentTranslation(params, manifestParts, bodySegments);
        } catch (err) {
          // Never let the new path be strictly worse than the old one: fall
          // back to the single-call + continuation flow below.
          console.error('[Translator] whole-document mode failed, falling back to continuation', err);
        }
      }
    }
  }

  const initialUserMessage = buildUserMessage(params.documentContent, params.targetLanguage);

  // ── Outer continuation state — shared across all passes ──────────────────
  let globalAccumulated = '';
  let globalInputTokens: number | undefined;
  let globalOutputTokens: number | undefined;
  let globalReasoningTokens: number | undefined;
  let globalModelUsed = '';
  // MAX_CONTINUATION_PASSES / MIN_COMPLETENESS_RATIO / MIN_PROGRESS_CHARS are
  // module-level (2026-08-11) so the cross-provider fallback runs the identical
  // continuation discipline instead of returning after a single pass.

  // ── Completeness guard ───────────────────────────────────────────────────
  // Root cause of the long-document truncation bug: on the proxied stream path,
  // stop_reason/finish_reason is sometimes never observed before the stream
  // closes (message_stop / [DONE]), leaving stopReason === undefined. The old
  // code then treated that as "done" and returned a cleanly truncated half
  // document. We add a length-based safety net: a translation should be roughly
  // the same order of magnitude as its source manifest. If the accumulated
  // output is materially shorter than the source AND the previous pass still
  // produced meaningful new text, we force another continuation pass regardless
  // of whether a stop signal was captured.
  //
  // The source manifest length is the comparison anchor. We require the output
  // to reach at least MIN_COMPLETENESS_RATIO of the source length before we
  // accept an unsignalled stop as genuine completion. Translations to Spanish
  // are typically 1.1-1.3x the English source, so 0.85 is a conservative floor
  // that still trips on a half-finished document (ratio ~0.5) without
  // false-positiving on a legitimately complete one.
  const sourceLengthForCompleteness = params.documentContent.length;


  for (let pass = 0; pass < MAX_CONTINUATION_PASSES; pass++) {
  // Pass 0 → fresh single-user message.
  // Pass N → [user, assistant(prior output), user(continue instruction)]
  const buildMessages = (): Array<{ role: string; content: string }> => {
    if (pass === 0) {
      return [{ role: 'user', content: initialUserMessage }];
    }
    return [
      { role: 'user', content: initialUserMessage },
      { role: 'assistant', content: globalAccumulated },
      { role: 'user', content: 'Continue the translation exactly from where you left off. Output only the continuation — no preamble, no repeated text, no explanation.' },
    ];
  };

  // Kept as a local alias so references inside the existing streaming logic still compile
  const userMessage = initialUserMessage;

  // Anthropic Messages API request body factory with optional extended thinking
  const thinkingConfig = getThinkingConfig();
  // stopReason is set during streaming; used after the loop to decide whether to continue
  let stopReason: string | undefined;
  const makeBody = (model: string) => {
    const body: Record<string, unknown> = {
      model,
      max_tokens: TRANSLATION_MAX_TOKENS,
      stream: true,
      // Request usage data in streaming response — without this, OpenRouter doesn't include
      // token counts in SSE chunks, causing $0 usage recording.
      stream_options: { include_usage: true },
      messages: buildMessages(),
    };
    // Only inject extended thinking on the first pass — continuation passes skip it
    // to avoid re-incurring thinking cost / latency on every chunk.
    if (thinkingConfig && pass === 0) {
      body.thinking = thinkingConfig;
    }
    return body;
  };

  // D14 slice 5 / A2 (2026-08-26 ruling follow-through): the route name is
  // the routing input — mode:'task' on anthropicProxy. The server stamps the
  // SSOT translation ladder + the tier's configured per-rung effort (rung 0
  // pins xhigh — the legacy body sent no thinking outside TESTING_MODE, the
  // same accepted transition as the legal-doc flips on this very tier); no
  // model and no thinking leave the browser on this path. Continuation
  // passes ride the same route — the server stamps the same rung 0 every
  // pass, so a document never switches models mid-conversation.
  const makeTaskBody = () => ({
    mode: 'task',
    route: 'translation',
    max_tokens: TRANSLATION_MAX_TOKENS,
    stream: true,
    stream_options: { include_usage: true },
    messages: buildMessages(),
  });
  console.log(`[Translator] Extended thinking: ${ENABLE_EXTENDED_THINKING ? 'ENABLED (high)' : 'DISABLED'}`);
  console.log(`[Translator] Target language: ${params.targetLanguage}`);


  // Helper to delay for exponential backoff
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Send request with automatic retry for transient 500 errors
  const sendRequestWithRetry = async (model: string, maxRetries = 3, taskMode = false): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        // Exponential backoff: 1s, 2s, 4s
        const backoffMs = Math.pow(2, attempt - 1) * 1000;
        await delay(backoffMs);
      }

      try {
        // Use Firebase proxy to avoid CORS issues
        const proxyUrl = getAnthropicProxyUrl();
        const accountHint = knownAccountHint();
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Billed by the proxy (server meter) — see ./proxyMeter.
            ...SERVER_METER_HEADERS,
            ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
            // Who is calling — the signed-in user's ID token (security slice 6, ./proxyAuth).
            ...(await proxyAuthHeaders()),
            // API key is handled by the proxy server
            ...(taskMode ? { 'X-Prest0n-Task': 'translation' } : {}),
          },
          body: JSON.stringify({ ...(taskMode ? makeTaskBody() : makeBody(model)), meterSummary: TRANSLATION_METER_SUMMARY }),
          signal: params.signal,
        });

        // If successful or non-retryable error, return immediately
        if (response.ok || (response.status !== 500 && response.status !== 502 && response.status !== 503 && response.status !== 429)) {
          return response;
        }

        if (attempt === maxRetries - 1) {
          return response;
        }
      } catch (fetchErr) {
        lastError = fetchErr as Error;
        if (attempt === maxRetries - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  };

  // Display id for the usage card — seeded with the rung-0 hint and
  // overwritten by the SERVED id the stream chunks carry (R17-F doctrine).
  let modelUsed = CLAUDE_MODEL;

  // Task-mode request — the route name is the only routing input; the
  // server's stamped models array descends the SSOT translation tier
  // itself, so the INCIDENT-20260811 descent need (policy blocks and
  // transport failures must reach another provider) is satisfied
  // server-side. D14 slice 7 (2026-09-15) retired the legacy client
  // ladder, the cross-provider chain, and the deploy-skew fallback — a
  // failure here is the route's own verdict. An abort propagates.
  let response: Response;
  try {
    response = await sendRequestWithRetry(CLAUDE_MODEL, 3, true);
  } catch (taskErr: any) {
    if (!params.signal?.aborted) params.onError?.(taskErr as Error);
    throw taskErr;
  }

  if (!response.ok) {
    let errorData: Record<string, unknown> = {};
    try {
      errorData = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
    const message = (errorData?.error as Record<string, unknown>)?.message
      || `Apex request failed (${response.status})`;
    const err: Error & { status?: number; code?: string } = new Error(String(message));
    err.status = response.status;
    err.code = String((errorData?.error as Record<string, unknown>)?.type || `HTTP_${response.status}`);
    params.onError?.(err);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for streaming');
    params.onError?.(err);
    throw err;
  }

  // Record start time for response time calculation
  const startTime = Date.now();

  const decoder = new TextDecoder('utf-8');
  // accumulated = text produced in THIS pass only.
  // onChunk reports globalAccumulated + accumulated so the caller sees the running total.
  let accumulated = '';
  let buffer = '';
  const blockTypes = new Map<number, string>();
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let reasoningTokens: number | undefined;
  let thinkingCharsAccumulated = 0;

  // Timeout for each stream chunk read - prevents indefinite hangs on network issues
  const STREAM_CHUNK_TIMEOUT_MS = TRANSLATION_CHUNK_TIMEOUT_MS;

  // Total request timeout - prevents indefinite hangs even when chunks trickle in slowly
  // This is critical for tab dormancy scenarios where per-chunk timeouts may not fire correctly
  const TOTAL_REQUEST_TIMEOUT_MS = TRANSLATION_TOTAL_TIMEOUT_MS;
  let totalTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let isTimedOut = false;

  try {
    // Start total request timeout
    const totalTimeoutPromise = new Promise<never>((_, reject) => {
      totalTimeoutId = setTimeout(() => {
        isTimedOut = true;
        console.error(`[Translator] Total request timeout exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`);
        reject(new Error(`Translation timeout - total request exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`));
      }, TOTAL_REQUEST_TIMEOUT_MS);
    });

    // Wrap the streaming loop in a race with the total timeout
    const streamingPromise = (async () => {
    let firstChunkLogged = false;
    while (true) {
      // Check abort signal before each read to allow cancellation
      if (params.signal?.aborted) {
        console.log('[Translator] Abort signal detected - stopping stream');
        break;
      }

      // Add timeout to each read operation to prevent indefinite hangs
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Stream chunk timeout - no data received for 60 seconds')), STREAM_CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[Translator] Stream chunk timeout - aborting read loop');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      // Bytes arrived. The ONLY liveness signal that survives extended
      // thinking: thinking_delta events carry no text_delta, so onChunk stays
      // silent for minutes while the socket is busy (INCIDENT-20260815).
      params.onStreamActivity?.();

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue;

        if (trimmed.startsWith('event: ')) {
          continue;
        }

        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);

          if (parsed.type === 'content_block_start') {
            if (typeof parsed.index === 'number' && parsed.content_block?.type) {
              blockTypes.set(parsed.index, parsed.content_block.type);
            }
          } else if (parsed.type === 'content_block_delta') {
            const blockType = typeof parsed.index === 'number' ? blockTypes.get(parsed.index) : undefined;
            // Handle thinking_delta for extended thinking content
            if (parsed.delta?.type === 'thinking_delta' || blockType === 'thinking') {
              const thinkingText = parsed.delta?.thinking || parsed.delta?.text || '';
              if (thinkingText) {
                thinkingCharsAccumulated += thinkingText.length;
              }
              continue;
            }
            if (parsed.delta?.type !== 'text_delta') continue;
            const delta = parsed.delta.text;
            if (delta) {
              accumulated += delta;
              params.onChunk?.(delta, accumulated);
              if (!firstChunkLogged) {
                firstChunkLogged = true;
              }
            }
          } else if (parsed.type === 'content_block_stop') {
            if (typeof parsed.index === 'number') {
              blockTypes.delete(parsed.index);
            }
          } else if (parsed.type === 'message_start' && parsed.message?.usage) {
            inputTokens = parsed.message.usage.input_tokens;
            reasoningTokens =
              parsed.message.usage.reasoning_tokens ??
              parsed.message.usage.thinking_tokens ??
              reasoningTokens;
          } else if (parsed.type === 'message_delta' && parsed.usage) {
            outputTokens = parsed.usage.output_tokens;
            if (parsed.usage.reasoning_tokens !== undefined) {
              reasoningTokens = parsed.usage.reasoning_tokens;
            } else if (parsed.usage.thinking_tokens !== undefined) {
              reasoningTokens = parsed.usage.thinking_tokens;
            }
            // Capture stop_reason for continuation-loop decision
            if (parsed.delta?.stop_reason) {
              stopReason = parsed.delta.stop_reason;
              if (stopReason === 'max_tokens') {
                console.warn(`[Translator] stop_reason=max_tokens on pass ${pass} — will continue`);
              }
            }
          } else if (parsed.type === 'message_stop') {
            break;
          } else if (parsed.type === 'error') {
            const errMsg = parsed.error?.message || 'Unknown streaming error';
            const err = new Error(errMsg);
            params.onError?.(err);
            throw err;
          }

          // --- OpenAI / OpenRouter SSE format fallback ---
          // The anthropicProxy routes through OpenRouter which returns OpenAI-format chunks
          else if (parsed.choices && Array.isArray(parsed.choices)) {
            const choice = parsed.choices[0] as Record<string, any>;
            if (choice?.delta) {
              const content = (choice.delta.content as string) || '';
              if (content) {
                accumulated += content;
                params.onChunk?.(content, accumulated);
                if (!firstChunkLogged) {
                  firstChunkLogged = true;
                }
              }
            }
            // Capture OpenAI-format finish_reason for continuation-loop decision
            if (choice?.finish_reason && choice.finish_reason !== null) {
              stopReason = choice.finish_reason;
              if (stopReason === 'length') {
                console.warn(`[Translator] finish_reason=length on pass ${pass} — will continue`);
              }
            }
            if (parsed.model && !modelUsed) {
              modelUsed = parsed.model as string;
            }
            if (parsed.usage) {
              inputTokens = parsed.usage.prompt_tokens ?? inputTokens;
              outputTokens = parsed.usage.completion_tokens ?? outputTokens;
            }
          }
        } catch (parseErr) {
          if (String(parseErr).includes('Unknown streaming error')) {
            throw parseErr;
          }
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim() && buffer.trim().startsWith('data: ')) {
      try {
        const jsonStr = buffer.trim().slice(6);
        if (jsonStr === '[DONE]') {
          // OpenRouter end-of-stream marker
        } else {
        const parsed = JSON.parse(jsonStr);
        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          const delta = parsed.delta.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, globalAccumulated + accumulated);
          }
        } else if (parsed.choices && Array.isArray(parsed.choices)) {
          const choice = parsed.choices[0] as Record<string, any>;
          const content = choice?.delta?.content || '';
          if (content) {
            accumulated += content;
            params.onChunk?.(content, globalAccumulated + accumulated);
          }
        }
        if (parsed.type === 'message_delta' && parsed.usage) {
          outputTokens = parsed.usage.output_tokens;
        } else if (parsed.usage) {
          outputTokens = parsed.usage.completion_tokens ?? outputTokens;
        }
        }
      } catch {
        // Ignore
      }
    }

    return accumulated;
    })(); // End of streamingPromise

    // Race between streaming and total timeout
    await Promise.race([streamingPromise, totalTimeoutPromise]);

  } finally {
    // Clear the total timeout to prevent memory leaks
    if (totalTimeoutId) {
      clearTimeout(totalTimeoutId);
    }
    // Always release the reader lock
    try {
      reader.releaseLock();
    } catch {
      // Ignore - reader may already be released
    }
  }

  // If we got here due to timeout, throw the error
  if (isTimedOut) {
    const err = new Error(`Translation timeout - total request exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`);
    params.onError?.(err);
    throw err;
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  // If no reasoning tokens from usage, estimate from thinking content
  const estimatedThinkingTokens = thinkingCharsAccumulated > 0
    ? Math.ceil(thinkingCharsAccumulated / 4)
    : undefined;
  const finalReasoningTokens = reasoningTokens ?? estimatedThinkingTokens;

  // FALLBACK: If no usage data was received in the stream, estimate from content length
  // so we don't record $0 usage for successful streaming translations.
  if (inputTokens === undefined && outputTokens === undefined && finalText.length > 0) {
    console.warn('[Translator] No usage data in stream — estimating from content length');
    outputTokens = Math.ceil(finalText.length / 4);
    inputTokens = Math.ceil(outputTokens * 0.3);
  }

  // Build token usage metadata
  const tokenUsage: TokenUsageMetadata = {
    inputTokens,
    outputTokens,
    reasoningTokens: finalReasoningTokens,
    totalTokens: (inputTokens || 0) + (outputTokens || 0) + (finalReasoningTokens || 0) || undefined,
    model: modelUsed,
    provider: 'claude',
    responseTimeMs,
    timestamp: new Date(),
  };

  // ── Merge this pass into global state ───────────────────────────────────────
  const newCharsThisPass = finalText.length;
  globalAccumulated += finalText;
  if (modelUsed) globalModelUsed = modelUsed;
  globalInputTokens  = (globalInputTokens  ?? 0) + (inputTokens  ?? 0);
  globalOutputTokens = (globalOutputTokens ?? 0) + (outputTokens ?? 0);
  if (reasoningTokens !== undefined) {
    globalReasoningTokens = (globalReasoningTokens ?? 0) + reasoningTokens;
  }

  // Explicit truncation signal from the provider (Anthropic or OpenAI shape).
  const signalledTruncation = stopReason === 'max_tokens' || stopReason === 'length';

  // Completeness guard: detect a SILENT truncation where no stop signal was
  // captured but the output is far shorter than the source. This is the core
  // fix for the long-document half-translation bug.
  const completenessRatio = sourceLengthForCompleteness > 0
    ? globalAccumulated.length / sourceLengthForCompleteness
    : 1;
  const looksIncomplete = completenessRatio < MIN_COMPLETENESS_RATIO;
  // Only force a continuation on incompleteness if THIS pass actually produced
  // meaningful new text. If a pass adds < MIN_PROGRESS_CHARS, the model has
  // nothing left to give (genuinely done or stuck) — forcing more passes would
  // spin uselessly, so we accept the current output and stop.
  const madeProgress = newCharsThisPass >= MIN_PROGRESS_CHARS;
  const forcedByCompleteness = looksIncomplete && madeProgress;

  if (forcedByCompleteness && !signalledTruncation) {
    console.warn(
      `[Translator] No truncation signal but output looks incomplete on pass ${pass} ` +
      `(ratio ${completenessRatio.toFixed(2)} < ${MIN_COMPLETENESS_RATIO}, +${newCharsThisPass} chars) ` +
      `— forcing continuation`
    );
  }
  if (looksIncomplete && !madeProgress) {
    console.warn(
      `[Translator] Output still short (ratio ${completenessRatio.toFixed(2)}) but pass ${pass} ` +
      `added only ${newCharsThisPass} chars — model has no more to give; accepting current output`
    );
  }

  const needsContinuation = signalledTruncation || forcedByCompleteness;
  const hasMorePasses     = pass < MAX_CONTINUATION_PASSES - 1;

  if (!needsContinuation || !hasMorePasses) {
    // ── Final pass: build and return the aggregated result ───────────────────
    if (needsContinuation && !hasMorePasses) {
      console.warn(`[Translator] Reached max continuation passes (${MAX_CONTINUATION_PASSES}). Returning partial output.`);
    }
    const globalFinalText = globalAccumulated.trim();
    const globalTokenUsage: TokenUsageMetadata = {
      inputTokens:    globalInputTokens,
      outputTokens:   globalOutputTokens,
      reasoningTokens: globalReasoningTokens,
      totalTokens: ((globalInputTokens ?? 0) + (globalOutputTokens ?? 0) + (globalReasoningTokens ?? 0)) || undefined,
      model: globalModelUsed || modelUsed,
      provider: 'claude',
      responseTimeMs,
      timestamp: new Date(),
    };
    params.onComplete?.(globalFinalText, globalTokenUsage);
    return { text: globalFinalText, tokenUsage: globalTokenUsage };
  }

  // Otherwise loop to the next continuation pass.
  console.log(`[Translator] Continuing translation (pass ${pass + 1}/${MAX_CONTINUATION_PASSES})`);
  } // end for-loop

  // TypeScript requires a return after the loop even though every code path inside returns.
  throw new Error('[Translator] Continuation loop exhausted without returning — this should never happen.');
}

/**
 * Format translation API errors for user-friendly display.
 * Uses code names instead of provider names (Apex instead of Anthropic/Claude).
 */
export function formatTranslationError(err: unknown): string {
  const e = err as Record<string, unknown> | null;
  if (!e) return 'unknown error';
  // ── INCIDENT-20260811 ────────────────────────────────────────────────────
  // A provider POLICY block carries no customer-actionable detail — only an
  // errand on OUR provider account. The live failure reached a Fernando client
  // as "confirm your age at https://openrouter.ai/settings/preferences",
  // because this formatter only rewrote vendor WORDS (sanitizeProviderNames)
  // and passed URLs and instructions through untouched. Policy blocks now
  // collapse to fixed copy, and every other message is scrubbed of URLs, bare
  // domains and account instructions BEFORE the codename map runs.
  if (isProviderPolicyBlocked(e)) return PROVIDER_POLICY_USER_MESSAGE;
  const status = e.status ? `status ${e.status}` : '';
  const code = e.code ? `code ${e.code}` : '';
  const rawMessage = typeof e.message === 'string' ? e.message : 'translation request failed';
  const message =
    sanitizeProviderNames(scrubUpstreamErrorText(rawMessage)) || 'translation request failed';
  const parts = [status, code].filter(Boolean).join(', ');
  return parts ? `${message} (${parts})` : message;
}

// ============================================================================
// Translation Manifest Functions
// ============================================================================

/**
 * Build a translation manifest containing ALL translatable document text.
 * This ensures titles, dates, footers, and metadata are included for translation.
 */
export function buildTranslationManifest(params: {
  title: string;
  content: string;
  createdAt: Date;
  source: 'research' | 'formatted' | 'video_script' | 'document_analysis' | 'document_analysis_failed';
  /**
   * MT-QA-BUG-001: firm name from the active tenant profile. When omitted
   * the manifest falls back to the Fernando default (safe for the widget
   * path). Non-Fernando callers MUST pass this value.
   */
  firmName?: string;
}): TranslationManifest {
  const { title, content, createdAt, source } = params;
  const year = createdAt.getFullYear();
  const dateStr = createdAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // MT-QA-BUG-001: prefer tenant firm name; fall back to Fernando only when
  // caller provided no firmName (widget / public chat path).
  const firmName =
    (typeof params.firmName === 'string' && params.firmName.trim()) ||
    DEFAULT_FIRM_NAME;

  // Include "Generated by" for research reports (not formatted ones)
  const preparedBy = source === 'research'
    ? `Generated by: ${DEFAULT_GENERATED_BY} | Facilitated by: ${firmName}`
    : '';

  // Full copyright notice (for cover page)
  const copyrightNotice = `(c) ${year} ${firmName}. Generated by ${DEFAULT_GENERATED_BY}. Facilitated by ${firmName}. All rights reserved.`;

  // Short copyright (for page footers)
  const copyrightShort = `(c) ${year} ${firmName}. All rights reserved.`;

  return {
    title,
    date: dateStr,
    preparedBy,
    firmName,
    copyrightNotice,
    copyrightShort,
    pageNumberFormat: 'Page {current}',
    bodyContent: content,
  };
}

/**
 * Format a translation manifest into the structured text format for the translator.
 */
export function formatManifestForTranslation(manifest: TranslationManifest): string {
  return `===SOURCE_LANGUAGE===

===TITLE===
${manifest.title}
===DATE===
${manifest.date}
===PREPARED_BY===
${manifest.preparedBy}
===FIRM_NAME===
${manifest.firmName}
===COPYRIGHT_NOTICE===
${manifest.copyrightNotice}
===COPYRIGHT_SHORT===
${manifest.copyrightShort}
===PAGE_FORMAT===
${manifest.pageNumberFormat}
===BODY_CONTENT===
${manifest.bodyContent}
===END===`;
}

/**
 * Parse translated text back into a TranslatedManifest structure.
 * Falls back gracefully if sections are missing.
 */
export function parseTranslatedManifest(translatedText: string): TranslatedManifest {
  // Helper to extract content between section markers
  const extractSection = (sectionName: string): string => {
    const startMarker = `===${sectionName}===`;
    const startIdx = translatedText.indexOf(startMarker);
    if (startIdx === -1) return '';

    const contentStart = startIdx + startMarker.length;

    // Find the next section marker or END
    const nextMarkerMatch = translatedText.slice(contentStart).match(/===[A-Z_]+==/);
    const endIdx = nextMarkerMatch
      ? contentStart + (nextMarkerMatch.index ?? translatedText.length)
      : translatedText.length;

    return translatedText.slice(contentStart, endIdx).trim();
  };

  const detectedSourceLanguage = extractSection('SOURCE_LANGUAGE') || 'Unknown';
  const title = extractSection('TITLE');
  const date = extractSection('DATE');
  const preparedBy = extractSection('PREPARED_BY');
  const firmName = extractSection('FIRM_NAME');
  const copyrightNotice = extractSection('COPYRIGHT_NOTICE');
  const copyrightShort = extractSection('COPYRIGHT_SHORT');
  const pageNumberFormat = extractSection('PAGE_FORMAT');
  const bodyContent = extractSection('BODY_CONTENT');

  // If we couldn't parse the structured format, fall back to treating the entire text as body content
  const hasStructuredContent = title || bodyContent;

  return {
    detectedSourceLanguage,
    title: title || 'Document',
    date: date || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    preparedBy: preparedBy || '',
    firmName: firmName || DEFAULT_FIRM_NAME,
    copyrightNotice: copyrightNotice || `(c) ${new Date().getFullYear()} ${DEFAULT_FIRM_NAME}. All rights reserved.`,
    copyrightShort: copyrightShort || `(c) ${new Date().getFullYear()} ${DEFAULT_FIRM_NAME}. All rights reserved.`,
    pageNumberFormat: pageNumberFormat || 'Page {current}',
    bodyContent: hasStructuredContent ? bodyContent : translatedText.trim(),
  };
}

/**
 * Extract just the body content from a translated manifest for streaming preview.
 * This is useful during streaming when we want to show the content progressively.
 */
export function extractBodyContentFromPartialTranslation(partialText: string): string {
  const bodyMarker = '===BODY_CONTENT===';
  const bodyIdx = partialText.indexOf(bodyMarker);

  if (bodyIdx === -1) {
    // Haven't reached body content yet - return empty or check if it's unstructured
    if (!partialText.includes('===')) {
      // Might be unstructured translation - return as-is
      return partialText;
    }
    return '';
  }

  const contentStart = bodyIdx + bodyMarker.length;
  const endMarker = '===END===';
  const endIdx = partialText.indexOf(endMarker, contentStart);

  return endIdx !== -1
    ? partialText.slice(contentStart, endIdx).trim()
    : partialText.slice(contentStart).trim();
}

// ============================================================================
// Simplified Document Transcription Translation
// ============================================================================

/**
 * Build a simplified system prompt for translating document transcriptions.
 * This is used when we have content from document analysis that needs direct translation
 * without the full manifest overhead.
 */
function buildSimplifiedTranslationPrompt(targetLanguage: string): string {
  return `You are a professional translator. Translate the following document content to ${targetLanguage}.

CRITICAL INSTRUCTIONS:
1. Translate ALL text to ${targetLanguage}
2. Preserve the EXACT original formatting, structure, paragraphs, and line breaks
3. Keep proper nouns (names, places, organizations) unchanged
4. Output ONLY the translation - no explanations, no notes, no introductory text
5. Start your response DIRECTLY with the translated content
6. Do NOT add any headers like "TRANSLATION:", "TRANSLATED CONTENT:", etc.
7. Do NOT add any framing text, just output the translation word-for-word
8. Maintain the same visual layout as the original

Begin translating:`;
}

/**
 * Parameters for simplified document transcription translation.
 */
export interface TranslateDocumentTranscriptionParams {
  /**
   * Transport liveness: fires whenever BYTES arrive, including extended-thinking
   * and keep-alive traffic that carries no text delta. onChunk cannot serve this
   * role — it requires content, and a high-effort model is silent on content for
   * minutes while it reasons. Three aborted 200-page runs came from treating
   * "no content" as "no life" (INCIDENT-20260815).
   */
  onStreamActivity?: () => void;

  /** The document transcription text to translate */
  transcription: string;
  /** The target language (e.g., "English", "Spanish", "French") */
  targetLanguage: string;
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text and token usage */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

/**
 * Result from simplified document transcription translation.
 */
export interface TranslateDocumentTranscriptionResult {
  /** The translated content */
  translatedContent: string;
  /** Detected source language (estimated from content) */
  detectedLanguage: string;
  /** Token usage metadata */
  tokenUsage?: TokenUsageMetadata;
}

/**
 * Translate document transcription content directly to a target language.
 * This is a simplified translation path that doesn't use the full manifest structure.
 * Used when translating content from document analysis where we only need to
 * translate the transcription portion.
 */
export async function translateDocumentTranscription(
  params: TranslateDocumentTranscriptionParams
): Promise<TranslateDocumentTranscriptionResult> {
  const systemPrompt = buildSimplifiedTranslationPrompt(params.targetLanguage);
  const userMessage = `${systemPrompt}\n\n${params.transcription}`;

  // Use same model selection and thinking config as main translator
  const thinkingConfig = getThinkingConfig();
  const makeBody = (model: string) => {
    const body: Record<string, unknown> = {
      model,
      max_tokens: TRANSLATION_MAX_TOKENS,
      stream: true,
      // Request usage data in streaming response — without this, OpenRouter doesn't include
      // token counts in SSE chunks, causing $0 usage recording.
      stream_options: { include_usage: true },
      messages: [
        {
          role: 'user',
          content: userMessage,
        },
      ],
    };
    if (thinkingConfig) {
      body.thinking = thinkingConfig;
    }
    return body;
  };

  // D14 slice 5 / A3: same route-name flip as runTranslation (A2) — the
  // server stamps the translation ladder + effort; model/thinking feed only
  // the marked deploy-skew fallback below.
  const makeTaskBody = () => ({
    mode: 'task',
    route: 'translation',
    max_tokens: TRANSLATION_MAX_TOKENS,
    stream: true,
    stream_options: { include_usage: true },
    messages: [
      {
        role: 'user',
        content: userMessage,
      },
    ],
  });

  console.log(`[Translator] Simplified translation to: ${params.targetLanguage}`);


  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const sendRequestWithRetry = async (model: string, maxRetries = 3, taskMode = false): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        const backoffMs = Math.pow(2, attempt - 1) * 1000;
        await delay(backoffMs);
      }

      try {
        // Use Firebase proxy to avoid CORS issues
        const proxyUrl = getAnthropicProxyUrl();
        const accountHint = knownAccountHint();
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Billed by the proxy (server meter) — see ./proxyMeter.
            ...SERVER_METER_HEADERS,
            ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
            // Who is calling — the signed-in user's ID token (security slice 6, ./proxyAuth).
            ...(await proxyAuthHeaders()),
            // API key is handled by the proxy server
            ...(taskMode ? { 'X-Prest0n-Task': 'translation' } : {}),
          },
          body: JSON.stringify({ ...(taskMode ? makeTaskBody() : makeBody(model)), meterSummary: TRANSLATION_METER_SUMMARY }),
          signal: params.signal,
        });

        if (response.ok || (response.status !== 500 && response.status !== 502 && response.status !== 503 && response.status !== 429)) {
          return response;
        }

        if (attempt === maxRetries - 1) {
          return response;
        }
      } catch (fetchErr) {
        lastError = fetchErr as Error;
        if (attempt === maxRetries - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  };

  // Display id for the usage card — seeded with the rung-0 hint and
  // overwritten by the SERVED id the stream chunks carry (R17-F doctrine).
  let modelUsed = CLAUDE_MODEL;

  // Task-mode request (see runTranslation). D14 slice 7 (2026-09-15)
  // retired the legacy ladder, the cross-provider chain, and the
  // deploy-skew fallback — the server's stamped models array descends the
  // SSOT tier itself, which is where the INCIDENT-20260811 descent now
  // lives. An abort propagates.
  let response: Response;
  try {
    response = await sendRequestWithRetry(CLAUDE_MODEL, 3, true);
  } catch (taskErr: any) {
    if (!params.signal?.aborted) params.onError?.(taskErr as Error);
    throw taskErr;
  }

  if (!response.ok) {
    let errorData: Record<string, unknown> = {};
    try {
      errorData = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
    const message = (errorData?.error as Record<string, unknown>)?.message
      || `Apex request failed (${response.status})`;
    const err: Error & { status?: number; code?: string } = new Error(String(message));
    err.status = response.status;
    err.code = String((errorData?.error as Record<string, unknown>)?.type || `HTTP_${response.status}`);
    params.onError?.(err);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for streaming');
    params.onError?.(err);
    throw err;
  }

  const startTime = Date.now();
  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  const blockTypes = new Map<number, string>();
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let reasoningTokens: number | undefined;
  let thinkingCharsAccumulated = 0;

  // Timeout for each stream chunk read - prevents indefinite hangs on network issues
  const STREAM_CHUNK_TIMEOUT_MS_2 = TRANSLATION_CHUNK_TIMEOUT_MS;

  // Total request timeout - prevents indefinite hangs even when chunks trickle in slowly
  // This is critical for tab dormancy scenarios where per-chunk timeouts may not fire correctly
  const TOTAL_REQUEST_TIMEOUT_MS_2 = TRANSLATION_TOTAL_TIMEOUT_MS;
  let totalTimeoutId2: ReturnType<typeof setTimeout> | null = null;
  let isTimedOut2 = false;

  try {
    // Start total request timeout
    const totalTimeoutPromise2 = new Promise<never>((_, reject) => {
      totalTimeoutId2 = setTimeout(() => {
        isTimedOut2 = true;
        console.error(`[Translator Lite] Total request timeout exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`);
        reject(new Error(`Translation timeout - total request exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`));
      }, TOTAL_REQUEST_TIMEOUT_MS_2);
    });

    // Wrap the streaming loop in a race with the total timeout
    const streamingPromise2 = (async () => {
    while (true) {
      // Check abort signal before each read to allow cancellation
      if (params.signal?.aborted) {
        console.log('[Translator Lite] Abort signal detected - stopping stream');
        break;
      }

      // Add timeout to each read operation (with OpenAI/OpenRouter SSE support)
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Stream chunk timeout - no data received for 60 seconds')), STREAM_CHUNK_TIMEOUT_MS_2);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[Translator Lite] Stream chunk timeout - aborting read loop');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      // Bytes arrived. The ONLY liveness signal that survives extended
      // thinking: thinking_delta events carry no text_delta, so onChunk stays
      // silent for minutes while the socket is busy (INCIDENT-20260815).
      params.onStreamActivity?.();

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue;
        if (trimmed.startsWith('event: ')) continue;
        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);

          if (parsed.type === 'content_block_start') {
            if (typeof parsed.index === 'number' && parsed.content_block?.type) {
              blockTypes.set(parsed.index, parsed.content_block.type);
            }
          } else if (parsed.type === 'content_block_delta') {
            const blockType = typeof parsed.index === 'number' ? blockTypes.get(parsed.index) : undefined;
            if (parsed.delta?.type === 'thinking_delta' || blockType === 'thinking') {
              const thinkingText = parsed.delta?.thinking || parsed.delta?.text || '';
              if (thinkingText) {
                thinkingCharsAccumulated += thinkingText.length;
              }
              continue;
            }
            if (parsed.delta?.type !== 'text_delta') continue;
            const delta = parsed.delta.text;
            if (delta) {
              accumulated += delta;
              params.onChunk?.(delta, accumulated);
            }
          } else if (parsed.type === 'content_block_stop') {
            if (typeof parsed.index === 'number') {
              blockTypes.delete(parsed.index);
            }
          } else if (parsed.type === 'message_start' && parsed.message?.usage) {
            inputTokens = parsed.message.usage.input_tokens;
            reasoningTokens = parsed.message.usage.reasoning_tokens ?? parsed.message.usage.thinking_tokens ?? reasoningTokens;
          } else if (parsed.type === 'message_delta' && parsed.usage) {
            outputTokens = parsed.usage.output_tokens;
            if (parsed.usage.reasoning_tokens !== undefined) {
              reasoningTokens = parsed.usage.reasoning_tokens;
            } else if (parsed.usage.thinking_tokens !== undefined) {
              reasoningTokens = parsed.usage.thinking_tokens;
            }
          } else if (parsed.type === 'message_stop') {
            break;
          } else if (parsed.type === 'error') {
            const errMsg = parsed.error?.message || 'Unknown streaming error';
            const err = new Error(errMsg);
            params.onError?.(err);
            throw err;
          }

          // --- OpenAI / OpenRouter SSE format fallback (Lite) ---
          else if (parsed.choices && Array.isArray(parsed.choices)) {
            const choice = parsed.choices[0] as Record<string, any>;
            if (choice?.delta) {
              const content = (choice.delta.content as string) || '';
              if (content) {
                accumulated += content;
                params.onChunk?.(content, accumulated);
              }
            }
            if (parsed.usage) {
              inputTokens = parsed.usage.prompt_tokens ?? inputTokens;
              outputTokens = parsed.usage.completion_tokens ?? outputTokens;
            }
          }
        } catch (parseErr) {
          if (String(parseErr).includes('Unknown streaming error')) {
            throw parseErr;
          }
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim() && buffer.trim().startsWith('data: ')) {
      try {
        const jsonStr = buffer.trim().slice(6);
        if (jsonStr === '[DONE]') {
          // OpenRouter end-of-stream marker — skip
        } else {
        const parsed = JSON.parse(jsonStr);
        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          const delta = parsed.delta.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }
        } else if (parsed.choices && Array.isArray(parsed.choices)) {
          const choice = parsed.choices[0] as Record<string, any>;
          const content = choice?.delta?.content || '';
          if (content) {
            accumulated += content;
            params.onChunk?.(content, accumulated);
          }
        }
        if (parsed.type === 'message_delta' && parsed.usage) {
          outputTokens = parsed.usage.output_tokens;
        } else if (parsed.usage) {
          outputTokens = parsed.usage.completion_tokens ?? outputTokens;
        }
        }
      } catch {
        // Ignore
      }
    }

    return accumulated;
    })(); // End of streamingPromise2

    // Race between streaming and total timeout
    await Promise.race([streamingPromise2, totalTimeoutPromise2]);

  } finally {
    // Clear the total timeout to prevent memory leaks
    if (totalTimeoutId2) {
      clearTimeout(totalTimeoutId2);
    }
    // Always release the reader lock
    try {
      reader.releaseLock();
    } catch {
      // Ignore - reader may already be released
    }
  }

  // If we got here due to timeout, throw the error
  if (isTimedOut2) {
    const err = new Error(`Translation timeout - total request exceeded ${TRANSLATION_TIMEOUT_DESCRIPTION}`);
    params.onError?.(err);
    throw err;
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  const estimatedThinkingTokens = thinkingCharsAccumulated > 0 ? Math.ceil(thinkingCharsAccumulated / 4) : undefined;
  const finalReasoningTokens = reasoningTokens ?? estimatedThinkingTokens;

  if (inputTokens === undefined && outputTokens === undefined && finalText.length > 0) {
    console.warn('[Translator:doc] No usage data in stream — estimating from content length');
    outputTokens = Math.ceil(finalText.length / 4);
    inputTokens = Math.ceil(outputTokens * 0.3);
  }

  const tokenUsage: TokenUsageMetadata = {
    inputTokens,
    outputTokens,
    reasoningTokens: finalReasoningTokens,
    totalTokens: (inputTokens || 0) + (outputTokens || 0) + (finalReasoningTokens || 0) || undefined,
    model: modelUsed,
    provider: 'claude',
    responseTimeMs,
    timestamp: new Date(),
  };

  params.onComplete?.(finalText, tokenUsage);

  // Try to detect source language from content characteristics
  // This is a simple heuristic - the actual source language detection happens in the main translator
  const detectedLanguage = detectSourceLanguage(params.transcription);

  return {
    translatedContent: finalText,
    detectedLanguage,
    tokenUsage,
  };
}
